import { useEffect } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder, faEllipsisV, faUsers, faLock, faPen, faFolderOpen } from '@fortawesome/free-solid-svg-icons'
import { useDroppable, useDraggable, useDndContext } from '@dnd-kit/core'
import { folderDragId, folderDropId } from 'dashboard/folders/folder-drag'
import { folderContentsLabel } from 'dashboard/folders/folder-contents-label'

const FolderListRow = ({
  folder,
  onClick,
  onEdit,
  onDelete,
  onShare,
  onMove,
  itemCount,
  subfolderCount = 0,
  sharedUserCount = 0,
  isMoving = false,
  acceptsDrag
}) => {
  const isOwner = folder.is_owner === true

  const { isOver, setNodeRef } = useDroppable({
    id: folderDropId(folder.id)
  })

  // Rows drag as well as receive, so a branch can be moved without switching to the grid.
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: folderDragId(folder.id),
    disabled: !isOwner || isMoving,
    data: { type: 'folder', folderId: folder.id, isOwner }
  })

  const { active } = useDndContext()
  const activeData = active?.data?.current
  const isDraggingOwnedItem = activeData?.isOwner !== false
  const canDrop =
    (isOwner || folder.is_editor) &&
    isDraggingOwnedItem &&
    (!acceptsDrag || acceptsDrag(folder, activeData))
  const isOverShared = isOver && !canDrop
  const isOverOwned = isOver && canDrop

  useEffect(() => {
    if (isOverShared) {
      document.body.classList.add('cursor-not-allowed')
    } else {
      document.body.classList.remove('cursor-not-allowed')
    }
    return () => document.body.classList.remove('cursor-not-allowed')
  }, [isOverShared])

  // All three item types, Reports included: `reports` is `dependent: :nullify`, so a folder that
  // looks empty because Reports were left out of the count deletes without warning and silently
  // unfiles them. Not gated on the Reports beta — the records are there either way.
  const totalItems =
    (folder.dashboards_count || 0) + (folder.audit_reports_count || 0) + (folder.reports_count || 0)
  const blockingCount = totalItems + subfolderCount

  // Two refs on one row: the drop target and the drag source are the same <tr>.
  const setRowRef = (node) => {
    setNodeRef(node)
    setDragRef(node)
  }

  return (
    <tr
      ref={setRowRef}
      {...listeners}
      {...attributes}
      onClick={() => onClick(folder)}
      style={{
        cursor: isOverShared ? 'not-allowed' : 'pointer',
        backgroundColor: isOverOwned ? '#e8f0fe' : isOverShared ? '#f8d7da' : undefined,
        opacity: isOverShared ? 0.7 : isDragging || isMoving ? 0.4 : 1
      }}
    >
      <td>
        <div className="d-flex align-items-center">
          <FontAwesomeIcon
            icon={subfolderCount > 0 ? faFolderOpen : faFolder}
            className="me-2"
            style={{ color: '#6c757d' }}
          />
          <div>
            <div>
              {folder.name}
              {!isOwner && (
                <span className="ms-2 badge bg-light text-muted" style={{ fontSize: '0.65rem', fontWeight: 'normal' }}>
                  <FontAwesomeIcon icon={folder.is_editor ? faPen : faLock} className="me-1" />
                  {folder.is_editor ? 'Editor' : 'View only'}
                </span>
              )}
            </div>
            {!isOwner && folder.owner && (
              <div>
                <small className="text-muted fst-italic">
                  Shared by {folder.owner.first_name} {folder.owner.last_name}
                </small>
              </div>
            )}
          </div>
        </div>
      </td>
      <td>
        <small className="text-muted">
          {subfolderCount > 0 && (
            <>
              {subfolderCount} {subfolderCount === 1 ? 'folder' : 'folders'}
              {' '}&bull;{' '}
            </>
          )}
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
          {sharedUserCount > 0 && (
            <>
              {' '}&bull;{' '}
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              {sharedUserCount} {sharedUserCount === 1 ? 'user' : 'users'}
            </>
          )}
        </small>
      </td>
      <td
        onClick={(e) => e.stopPropagation()}
        // dnd-kit claims pointerdown on the row; without this the dropdown never opens.
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Dropdown>
          <Dropdown.Toggle
            variant="link"
            className="text-muted p-0 border-0"
            bsPrefix="btn"
            data-testid={`folder-menu-${folder.id}`}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {isOwner && (
              <Dropdown.Item onClick={() => onShare(folder)}>
                Share
              </Dropdown.Item>
            )}
            {isOwner && (
              <Dropdown.Item onClick={() => onEdit(folder)}>
                Rename
              </Dropdown.Item>
            )}
            {isOwner && onMove && (
              <Dropdown.Item onClick={() => onMove(folder)}>
                Move to folder
              </Dropdown.Item>
            )}
            {isOwner && (
              <Dropdown.Item
                onClick={() => onDelete(folder)}
                className={blockingCount > 0 ? 'text-muted' : 'text-danger'}
                disabled={blockingCount > 0}
              >
                Delete
                {blockingCount > 0 && (
                  <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>
                    ({folderContentsLabel(subfolderCount, totalItems)})
                  </span>
                )}
              </Dropdown.Item>
            )}
            {!isOwner && (
              <Dropdown.Item disabled className="text-muted">
                No actions available
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  )
}

export default FolderListRow
